/** Align with API `PRODUCT_DESCRIPTION_MAX_LENGTH` (512 KB). */
export const PRODUCT_DESCRIPTION_MAX_LENGTH = 512_000;

/** Align with API `UpdateAdminProductDto` / `CreateAdminProductDto` sku max length. */
export const PRODUCT_SKU_MAX_LENGTH = 100;

function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Truncates slug-like SKUs generated from long product titles.
 */
export function sanitizeProductSkuForApi(sku: unknown): string | undefined {
  if (sku == null) return undefined;
  const trimmed = String(sku).trim();
  if (!trimmed) return undefined;
  if (trimmed.length <= PRODUCT_SKU_MAX_LENGTH) return trimmed;
  return trimmed.slice(0, PRODUCT_SKU_MAX_LENGTH);
}

/**
 * Accepts full URLs and common YouTube shorthand (`watch?v=…`); drops invalid values.
 */
export function normalizeProductVideoUrlForApi(
  videoUrl: unknown,
): string | undefined {
  const raw = String(videoUrl ?? "").trim();
  if (!raw) return undefined;

  if (isValidHttpUrl(raw)) return raw;

  const watchParam = raw.match(/^watch\?v=([a-zA-Z0-9_-]{6,})/i);
  if (watchParam) {
    return `https://www.youtube.com/watch?v=${watchParam[1]}`;
  }

  if (/^[a-zA-Z0-9_-]{11}$/.test(raw)) {
    return `https://www.youtube.com/watch?v=${raw}`;
  }

  const withScheme = raw.startsWith("//")
    ? `https:${raw}`
    : /^[\w.-]+\.[a-z]{2,}/i.test(raw)
      ? `https://${raw}`
      : raw;

  return isValidHttpUrl(withScheme) ? withScheme : undefined;
}

/**
 * Shrinks pasted marketplace HTML (inline styles, scripts) before product save API calls.
 */
export function sanitizeProductDescriptionForApi(
  description: unknown,
): string | undefined {
  if (description == null) return undefined;
  let html = String(description);
  if (!html.trim()) return "";

  html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  html = html.replace(/\sstyle\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  html = html.replace(/\sclass\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  html = html.replace(/\sdata-[a-z0-9_-]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");

  if (html.length > PRODUCT_DESCRIPTION_MAX_LENGTH) {
    html = html.slice(0, PRODUCT_DESCRIPTION_MAX_LENGTH);
  }
  return html;
}

export function prepareProductPayloadForApi<T extends Record<string, unknown>>(
  data: T,
): T {
  let next: Record<string, unknown> = { ...data };

  if ("description" in next) {
    const description = sanitizeProductDescriptionForApi(next.description);
    if (description !== undefined) {
      next = { ...next, description };
    }
  }

  if ("sku" in next) {
    const sku = sanitizeProductSkuForApi(next.sku);
    if (sku !== undefined) {
      next = { ...next, sku };
    } else {
      const { sku: _removed, ...rest } = next;
      next = rest;
    }
  }

  if ("videoUrl" in next) {
    const videoUrl = normalizeProductVideoUrlForApi(next.videoUrl);
    if (videoUrl !== undefined) {
      next = { ...next, videoUrl };
    } else {
      const { videoUrl: _removed, ...rest } = next;
      next = rest;
    }
  }

  return next as T;
}

export function getProductSaveErrorMessage(error: unknown): string {
  const err = error as {
    response?: { status?: number; data?: { message?: string | string[] } };
    status?: number;
    message?: string;
  };
  const status = err.response?.status ?? err.status;
  if (status === 413) {
    return "Product description is too large. Remove extra pasted HTML or images from the description and try again.";
  }
  const raw = err.response?.data?.message;
  if (raw != null) {
    return Array.isArray(raw) ? raw.join(" ") : String(raw);
  }
  if (typeof err.message === "string" && err.message.trim()) {
    return err.message;
  }
  return "Failed to save product. Please try again.";
}
